import * as React from 'react';
import { Typography, LinearProgress, styled, Box, useTheme, CardActions, TablePagination, Tooltip } from '@mui/material';
import { IAlertAnalytic } from '../../../../dto/Interfaces/Analytics';
import Column from '../../../../components/Column';
import Row from '../../../../components/Row';
import { colors } from '../../../../utils/palette';
import ReactCountryFlag from 'react-country-flag';
import { countries } from '../../../../utils/countries';
import { dotsFormat } from '../../../../utils/functions';

export interface ProgressChartProps {
  data: IAlertAnalytic[];
  type: 'progressChart';
  rowsPerPage?: number;
}

const ProgressChart: React.FC<ProgressChartProps> = ({ data, rowsPerPage = 7 }) => {
  const theme = useTheme();
  const [page, setPage] = React.useState(0);

  const handleChangePage = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const upperLimit = page === 0 ? page + 1 * 6 : (page + 1) * rowsPerPage - 1;
  const lowerLimit = page === 0 ? page : page * rowsPerPage;

  const percentageFormatNoZeroReturned = (x: number): string => {
    if (x < 1 && x > 0) {
      return '<1%';
    }
    if (x > 99 && x < 100) {
      return '>99%';
    }
    return `${Math.floor(x)}%`;
  };
  const printCountryLabel = (label: string) => {
    if (label && countries.filter((e) => e.code === label)[0]) {
      return countries.filter((e) => e.code === label)[0]?.name;
    }
    return '-';
  };
  return (
    <PaddedColumn>
      <Box component="div" height="100%">
        {data
          .filter((_el, i) => i >= lowerLimit && i <= upperLimit)
          .map(({ label, hits, percentage_formatted, percentage, total, tooltip }, i) => {
            const content = (
              <Box component="div" key={`alerts-${i}-tooltip`} style={{ width: '100%' }}>
                <Row key={`alerts-${i}`} testId="alerts-test" centered hAlign="flex-start" style={{ marginBottom: theme.spacing(1) }}>
                  <Column grow centered>
                    <Row style={{ width: '100%' }} vAlign="center" hAlign="space-between">
                      <Box component="div" style={{ display: 'flex', alignItems: 'center' }}>
                        {tooltip && printCountryLabel(label) !== '-' && (
                          <ReactCountryFlag
                            countryCode={label}
                            svg
                            style={{
                              fontSize: 28,
                              marginRight: 10,
                              marginBottom: 1,
                              borderRadius: 7,
                            }}
                          />
                        )}

                        <Typography gutterBottom={true} variant="h6">
                          {tooltip && printCountryLabel(label) !== '-' ? printCountryLabel(label) : label}
                        </Typography>
                      </Box>
                      <>
                        {total && (
                          <Typography variant="body2" color="textSecondary">
                            {percentage_formatted || percentageFormatNoZeroReturned(hits)}
                          </Typography>
                        )}
                      </>
                    </Row>

                    <CustomLinearProgress variant="determinate" valueBuffer={2} value={percentage || hits} />
                  </Column>
                </Row>
              </Box>
            );
            if (!tooltip) return content;
            return (
              <Tooltip
                key="alerts-tooltip"
                title={
                  <Box style={{ padding: theme.spacing(1) }} sx={{ width: 150 }} height="100%">
                    <Typography style={{ color: colors.white, marginBottom: theme.spacing(1), fontWeight: 600 }}>{label}</Typography>
                    {Object.entries(tooltip).map(([key, value]) => (
                      <Row key={key}>
                        <Typography
                          variant="body2"
                          style={{
                            color: colors.white,
                            marginRight: theme.spacing(1),
                            width: '100px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {key}:
                        </Typography>

                        <Typography variant="body2" style={{ color: colors.white }}>
                          {dotsFormat(value)}
                        </Typography>
                      </Row>
                    ))}
                  </Box>
                }
                componentsProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: theme.palette.grey[300],
                    },
                  },
                }}
                PopperProps={{
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [-350, -50],
                      },
                    },
                  ],
                }}
              >
                {content}
              </Tooltip>
            );
          })}
      </Box>
      <CardActions style={{ justifyContent: 'flex-end' }}>
        <TablePagination
          component="div"
          count={data.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[rowsPerPage]}
          onPageChange={handleChangePage}
        />
      </CardActions>
    </PaddedColumn>
  );
};

export default ProgressChart;

const PaddedColumn = styled(Column)(() => ({
  height: '100%',
}));

const CustomLinearProgress = styled(LinearProgress)(({ theme }) => ({
  width: '100%',
  height: 10,
  '& .MuiLinearProgress-barColorPrimary': {
    backgroundColor: theme.palette.primary.main,
  },
}));
